import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  HardHat,
  Wrench,
  Droplet,
  Zap,
  Hammer,
  PaintRoller,
  Grid3x3,
  Users,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Award,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import SalesChatWidget from "../components/SalesChatWidget";

const trades = [
  { icon: HardHat, label: "Safety & OSHA", code: "01" },
  { icon: Wrench, label: "Tool Mastery", code: "02" },
  { icon: Droplet, label: "Plumbing Basics", code: "03" },
  { icon: Zap, label: "Electrical Basics", code: "04" },
  { icon: Hammer, label: "Carpentry", code: "05" },
  { icon: PaintRoller, label: "Drywall & Paint", code: "06" },
  { icon: Grid3x3, label: "Tiling", code: "07" },
  { icon: Users, label: "Customer Service", code: "08" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cta = () => navigate(user ? "/pricing" : "/register");

  return (
    <div className="min-h-screen bg-white text-slate-950" data-testid="landing-page">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="w-8 h-8 bg-orange-600 grid place-items-center rounded-sm">
              <Award className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black tracking-tight text-lg">
              PLATINUM<span className="text-orange-600">1</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
            <a href="#program" className="hover:text-orange-600 transition-colors">Program</a>
            <a href="#modules" className="hover:text-orange-600 transition-colors">Modules</a>
            <a href="#how" className="hover:text-orange-600 transition-colors">How It Works</a>
            <Link to="/pricing" data-testid="nav-pricing-link" className="hover:text-orange-600 transition-colors">Pricing</Link>
            <Link to="/blog" data-testid="nav-blog-link" className="hover:text-orange-600 transition-colors">Blog</Link>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="text-sm bg-orange-600 text-white px-4 py-2 rounded-sm font-semibold hover:bg-orange-700 transition-colors"
              >
                My Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="text-sm font-semibold text-slate-900 hover:text-orange-600">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register-link"
                  className="text-sm bg-orange-600 text-white px-4 py-2 rounded-sm font-semibold hover:bg-orange-700 transition-colors"
                >
                  Get Certified
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200" id="program">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
              <span className="w-2 h-2 bg-orange-600 inline-block" /> Industry-grade certification
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-slate-950">
              The handyman <br />
              standard, <span className="text-orange-600">verified.</span>
            </h1>
            <p className="mt-7 text-lg text-slate-600 max-w-xl leading-relaxed">
              A rigorous 8-module curriculum and proctored test program that certifies construction
              workers as <strong className="text-slate-900">Platinum 1 Certified</strong> — the credential
              homeowners, GCs, and property managers actually look for.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={cta}
                data-testid="hero-get-certified-btn"
                className="group bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 rounded-sm font-semibold tracking-wide inline-flex items-center gap-2 transition-colors"
              >
                Start the Program <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#modules"
                data-testid="hero-modules-link"
                className="px-7 py-4 rounded-sm font-semibold text-slate-900 border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                See the 8 modules
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["8", "Modules"],
                ["75%", "Pass score"],
                ["PDF", "Certificate"],
              ].map(([n, l]) => (
                <div key={l} className="border-l-2 border-orange-600 pl-3">
                  <div className="font-display text-3xl font-black tracking-tighter">{n}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual side */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] bg-slate-100 overflow-hidden rounded-sm">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=900"
                alt="Construction worker"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Cohort 01</div>
                <div className="font-display text-2xl font-bold leading-tight mt-1">Built on the jobsite, not the classroom.</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-sm p-4 shadow-sm hidden md:flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-600 grid place-items-center rounded-sm">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">OSHA-Aligned</div>
                <div className="font-display font-bold text-slate-900">Safety-First Curriculum</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="border-t border-slate-200 bg-slate-50 py-3 overflow-hidden">
          <div className="marquee gap-12 text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              ["Plumbing", "Electrical", "Carpentry", "Drywall", "Tiling", "Safety", "Tools", "Service"].map((w) => (
                <span key={`${k}-${w}`} className="flex items-center gap-12">
                  <span>{w}</span>
                  <span className="w-1 h-1 bg-orange-600 inline-block" />
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">The Curriculum</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-3">
              Eight modules. <br /> One credential.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-slate-600 leading-relaxed text-lg">
              Every Platinum 1 Certified handyman has demonstrated working knowledge across the eight skill
              domains that define safe, professional residential trade work — assessed by quiz, not by
              hours-of-attendance.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-slate-200">
          {trades.map((t) => (
            <div
              key={t.label}
              data-testid={`module-card-${t.code}`}
              className="border-b border-r border-slate-200 p-6 hover:bg-orange-50/40 transition-colors group"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="w-11 h-11 bg-slate-950 grid place-items-center rounded-sm group-hover:bg-orange-600 transition-colors">
                  <t.icon className="w-5 h-5 text-white" />
                </div>
                <span className="font-mono text-xs text-slate-400">/{t.code}</span>
              </div>
              <div className="font-display font-bold text-slate-950 text-lg leading-snug">{t.label}</div>
              <div className="text-xs text-slate-500 mt-2">Module {t.code}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-slate-950 text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-400">How It Works</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3">
              Three steps to certified.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              ["01", "Study", "Read the curriculum, watch the videos, and run through hands-on exercises for each of the 8 modules."],
              ["02", "Pass", "Complete each module's certification quiz with a 75% score or higher. Retakes allowed."],
              ["03", "Earn", "Receive a downloadable Platinum 1 Certified PDF with a unique credential ID."],
            ].map(([n, t, d]) => (
              <div key={n} className="border-t-2 border-orange-600 pt-5">
                <div className="font-mono text-orange-400 text-sm">/{n}</div>
                <div className="font-display font-bold text-2xl mt-2">{t}</div>
                <p className="text-slate-400 mt-3 leading-relaxed text-sm">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="border border-slate-200 rounded-sm p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-slate-50">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
              Ready to become <span className="text-orange-600">Platinum 1 Certified</span>?
            </h2>
            <p className="text-slate-600 mt-4 text-lg max-w-2xl">
              Free to start. Pass all 8 quizzes and download your certificate immediately.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
              {["Video + readable curriculum", "Auto-graded certification quizzes", "PDF certificate with credential ID", "Track progress across modules"].map((l) => (
                <li key={l} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-600" />{l}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <button
              onClick={cta}
              data-testid="cta-get-started-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 rounded-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/login" data-testid="cta-signin-link" className="px-6 py-4 rounded-sm font-semibold text-slate-900 border border-slate-300 hover:bg-white text-center">
              I already have an account
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} Platinum 1 Certified. A handyman certification program. · <Link to="/blog" className="hover:text-orange-600">Blog</Link>
      </footer>
      <SalesChatWidget />
    </div>
  );
}
